// src/api/eventConfig.ts
import { API_URL } from './config'

/** Identidade visual e configuração do evento, servidas pelo backend. */
export interface EventConfig {
  eventUid: string
  name: string
  shortName?: string
  timezone: string
  status: string
  venue?: { name?: string; city?: string; state?: string }
  branding: {
    primaryColor?: string
    secondaryColor?: string
    accentColor?: string
    backgroundColor?: string
    logoUrl?: string
  }
  settings: {
    /** Tipos de documento aceitos na busca. Configurável por evento. */
    documentosAceitos?: Array<'cpf' | 'cnpj' | 'rg'>
    [k: string]: unknown
  }
}

/** Tipos de documento do evento, com fallback seguro. */
export function documentosAceitos(c: EventConfig): Array<'cpf' | 'cnpj' | 'rg'> {
  const l = c.settings?.documentosAceitos
  return Array.isArray(l) && l.length ? l : ['cpf', 'cnpj']
}

/** Quantidades de dígitos válidas para os tipos aceitos. */
export function tamanhosValidos(tipos: Array<'cpf' | 'cnpj' | 'rg'>): number[] {
  const t: number[] = []
  if (tipos.includes('cpf')) t.push(11)
  if (tipos.includes('cnpj')) t.push(14)
  // RG não tem comprimento único: varia por estado, e alguns têm dígito não numérico.
  if (tipos.includes('rg')) t.push(7, 8, 9, 10)
  return t
}

/** Texto de instrução da tela, derivado dos tipos aceitos. */
export function rotuloDocumentos(tipos: Array<'cpf' | 'cnpj' | 'rg'>): string {
  const nomes = tipos.map(t => (t === 'cpf' ? 'CPF' : t === 'cnpj' ? 'CNPJ' : 'RG'))
  if (nomes.length === 1) return nomes[0]
  return `${nomes.slice(0, -1).join(', ')} ou ${nomes[nomes.length - 1]}`
}

/** Exemplo do formato esperado, mostrado no visor enquanto nada foi digitado. */
export function mascaraDocumento(tipos: Array<'cpf' | 'cnpj' | 'rg'>): string {
  // Quando o evento aceita mais de um tipo, mostra o do documento que a maioria digita:
  // o visor é exemplo, não regra — a validação segue aceitando todos os tamanhos.
  if (tipos.includes('cpf')) return '999.999.999-99'
  if (tipos.includes('cnpj')) return '99.999.999/9999-99'
  return '999999999'
}

export type LayoutTotem = 'classico' | 'amplo'

/**
 * Qual arranjo a tela do totem usa.
 *
 * 'classico' é o que os totens já usavam: logo, nome da operação, instrução escrita e
 * teclado com margem nas laterais. 'amplo' é o teclado ocupando quase toda a largura, sem
 * instrução — mais área de toque, para totem em pé operado de longe.
 *
 * Vem das settings do evento, e não de constante no código, porque vira campo no admin:
 * cada cliente escolhe sem precisar de build. Na ausência da chave, 'classico'.
 */
export function layoutTotem(c: EventConfig): LayoutTotem {
  return c.settings?.layoutTotem === 'amplo' ? 'amplo' : 'classico'
}

/** Instrução do totem: texto do evento quando houver, senão um derivado dos tipos aceitos. */
export function textoInstrucao(c: EventConfig, rotulo: string): string {
  const t = c.settings?.instrucaoTotem
  if (typeof t === 'string' && t.trim()) return t.trim()
  return `Informe seu ${rotulo} utilizando as teclas numéricas e aperte avançar para imprimir sua credencial.`
}

const CHAVE_CACHE = 'eventConfig:v1'
const CHAVE_ETAG = 'eventConfig:etag:v1'

/** Usado enquanto a primeira resposta não chega — evita piscar branco no totem. */
export const CONFIG_PADRAO: EventConfig = {
  eventUid: '',
  name: 'Credenciamento',
  timezone: 'America/Sao_Paulo',
  status: 'published',
  branding: {
    primaryColor: '#092D77',
    secondaryColor: '#38B7B4',
    accentColor: '#235C78',
    backgroundColor: '#FFFFFF',
  },
  settings: {},
}

function base(): string {
  return API_URL.replace(/\/+$/, '')
}

export function lerCache(): EventConfig | null {
  try {
    const bruto = localStorage.getItem(CHAVE_CACHE)
    return bruto ? (JSON.parse(bruto) as EventConfig) : null
  } catch {
    return null
  }
}

/**
 * Busca a configuração do evento, aproveitando o cache local.
 *
 * O servidor responde com ETag; quando nada mudou devolve 304 e o corpo não trafega.
 * O totem fica ligado o dia inteiro numa rede de centro de convenções — revalidar é
 * barato, rebaixar a configuração inteira a cada tela não é.
 *
 * O logo NÃO é buscado aqui: sua URL carrega o hash do conteúdo e o navegador a guarda
 * com cache imutável. Trocar o logo no admin muda a URL, e a troca se propaga sozinha.
 */
export async function carregarEventConfig(eventUid?: string): Promise<EventConfig> {
  const cache = lerCache()
  const uid = eventUid || cache?.eventUid
  // Sem evento conhecido, o backend resolve pelo DEFAULT_EVENT_UID da instância.
  const url = uid ? `${base()}/events/${uid}/settings` : `${base()}/events`

  try {
    const etag = localStorage.getItem(CHAVE_ETAG)
    const resp = await fetch(url, {
      headers: etag && uid ? { 'If-None-Match': etag } : {},
    })

    if (resp.status === 304 && cache) return cache

    if (!resp.ok) return cache || CONFIG_PADRAO

    const dados = await resp.json()
    // `GET /events` devolve lista; `GET /events/:uid/settings` devolve o objeto.
    const config: EventConfig = Array.isArray(dados)
      ? await carregarEventConfig(dados[0]?.uid)
      : dados

    const novoEtag = resp.headers.get('ETag')
    if (novoEtag) localStorage.setItem(CHAVE_ETAG, novoEtag)
    localStorage.setItem(CHAVE_CACHE, JSON.stringify(config))
    return config
  } catch {
    // Offline ou API fora: o totem continua operando com a última configuração vista.
    return cache || CONFIG_PADRAO
  }
}

/** URL absoluta do logo, ou null quando o evento não tem um configurado. */
export function urlDoLogo(config: EventConfig): string | null {
  const url = config.branding?.logoUrl
  if (!url) return null
  if (/^https?:\/\//i.test(url)) return url
  // O asset é servido pela raiz do backend, fora do prefixo /api/game.
  return base().replace(/\/api\/game$/, '') + url
}
